import * as React from 'react';

import {Routing} from 'nutshell-core/routing';
import type {FilterObject} from 'nutshell-core/types';

import {CompletedActionsColorsFunnel} from 'shells/colors/reports';
import type {FunnelChartItem, DistributionBarItem} from 'shells/funnel-report/funnel-chart';
import {Stack} from 'shells/layout/stack/stack';
import {Body} from 'shells/typography/body';

import {
    DEFAULT_FIELDS_ENGAGEMENT_BAR_LEADS,
    DEFAULT_SORT_FIELD_ENGAGEMENT_BAR_LEADS,
} from '../report-constants';

import {ENGAGEMENT_ITEMS, SALE_ITEM_KEY} from './consts';
import type {EngagementItemKey} from './consts';
import type {FunnelStats} from './hooks/use-get-funnel-stats';

export const getTooltip = (description: string, example: string): React.ReactElement => {
    return (
        <Stack>
            <Body color='white' size='small'>
                {description}
            </Body>
            <Body color='white' size='small' isItalic={true}>
                {example}
            </Body>
        </Stack>
    );
};

// Define the order of action types for consistent display
const ACTION_TYPE_ORDER = ['Chats', 'Calls', 'Emails', 'Bookings', 'Quotes', 'Texts'];

/**
 * Creates distribution bar data for the actions breakdown.
 * Each action type gets an equal-width section with its count and color.
 */
const createActionsDistributionBar = (actionsBreakdown: {
    [actionType: string]: number;
}): DistributionBarItem[] => {
    return ACTION_TYPE_ORDER.map((actionType) => ({
        label: actionType,
        value: actionsBreakdown[actionType] || 0,
        // @ts-expect-error
        color: CompletedActionsColorsFunnel[actionType],
    }));
};

/**
 * Creates a list of FunnelChartItem objects by combining engagement items with their corresponding REST API funnel stats.
 * @param {FunnelStats} funnelStats - The funnel stats containing count data for each engagement item.
 * @returns {FunnelChartItem[]} - A list of FunnelChartItem objects combining static engagement item properties with dynamic funnel data.
 */
export const createFunnelChartItems = (
    funnelStats: FunnelStats,
    filters: FilterObject[],
    refs: Partial<Record<EngagementItemKey, HTMLDivElement | null | undefined>>,
    isEmbedded: boolean
): FunnelChartItem[] => {
    return ENGAGEMENT_ITEMS.map((item) => {
        let value: number;
        let valueSubtitle = item.valueSubtitle;
        let distributionBar;
        let url = '';

        switch (item.key) {
            case 'visitors':
                value = funnelStats.visitors;
                break;
            case 'opens':
                value = funnelStats.opens;
                break;
            case 'actions':
                value = funnelStats.actions;
                distributionBar = createActionsDistributionBar(funnelStats.actionsBreakdown);
                break;
            case 'leads':
                value = funnelStats.leads;
                url = buildFilteredLeadsUrl(false);
                break;
            case SALE_ITEM_KEY:
                value = funnelStats.sales.count;
                valueSubtitle = `${funnelStats.sales.totalValue} sales`;
                url = buildFilteredLeadsUrl(true);
                break;
            default:
                value = 0;
        }

        return {
            title: item.title,
            value,
            valueSubtitle,
            tooltip: getTooltip(item.tooltip.description, item.tooltip.example),
            distributionBar,
            onClickTitle: () => {
                if (refs[item.key]) {
                    // @ts-expect-error
                    refs[item.key].scrollIntoView({behavior: 'smooth', block: 'start'});
                }
            },
            onClickFunnelBar: url && !isEmbedded ? () => window.open(url, '_blank') : undefined,
            isHoverable: !isEmbedded && item.isHoverable,
        };
    });
};

export const buildFilteredLeadsUrl = (filterByWon?: boolean): string => {
    const urlParams: any = {
        list: {
            fields: {
                leads: DEFAULT_FIELDS_ENGAGEMENT_BAR_LEADS,
            },
            sort: DEFAULT_SORT_FIELD_ENGAGEMENT_BAR_LEADS,
            page: 1,
        },
        filter: {
            ...(filterByWon ? {status: 'Won'} : {}),
        },
    };

    const params = Routing.param(urlParams);

    return `/inbox/leads?${params}`;
};
